import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Phone, Smartphone, Laptop, Monitor, Headset, Wifi, Pen, BookOpen } from 'lucide-react';

const Hero = () => {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const [icons, setIcons] = useState([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 50;

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = Math.random() * 2 - 1;
        this.speedY = Math.random() * 2 - 1;
        this.opacity = Math.random() * 0.3 + 0.1;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }

      draw() {
        ctx.fillStyle = `rgba(15, 23, 42, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Floating icons with collision detection
  useEffect(() => {
    const iconData = [
      { id: 0, size: 64, x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight },
      { id: 1, size: 56, x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight },
      { id: 2, size: 72, x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight },
      { id: 3, size: 68, x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight },
      { id: 4, size: 60, x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight },
      { id: 5, size: 58, x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight },
      { id: 6, size: 54, x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight },
      { id: 7, size: 66, x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight },
    ].map(icon => ({
      ...icon,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      hue: Math.random() * 360,
      directionChangeCounter: Math.floor(Math.random() * 100)
    }));

    setIcons(iconData);

    const checkCollision = (icon1, icon2) => {
      const dx = icon1.x - icon2.x;
      const dy = icon1.y - icon2.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      return distance < (icon1.size + icon2.size) / 2;
    };

    const handleCollision = (icon1, icon2) => {
      const dx = icon2.x - icon1.x;
      const dy = icon2.y - icon1.y;
      const angle = Math.atan2(dy, dx);
      
      const speed1 = Math.sqrt(icon1.vx * icon1.vx + icon1.vy * icon1.vy);
      const speed2 = Math.sqrt(icon2.vx * icon2.vx + icon2.vy * icon2.vy);
      
      icon1.vx = -Math.cos(angle) * speed1;
      icon1.vy = -Math.sin(angle) * speed1;
      icon2.vx = Math.cos(angle) * speed2;
      icon2.vy = Math.sin(angle) * speed2;
    };

    const animateIcons = () => {
      setIcons(prevIcons => {
        const newIcons = prevIcons.map(icon => {
          let newX = icon.x + icon.vx;
          let newY = icon.y + icon.vy;
          let newVx = icon.vx;
          let newVy = icon.vy;

          // Wrap around screen edges - icons fly through
          if (newX < -icon.size) {
            newX = window.innerWidth + icon.size;
          } else if (newX > window.innerWidth + icon.size) {
            newX = -icon.size;
          }
          
          if (newY < -icon.size) {
            newY = window.innerHeight + icon.size;
          } else if (newY > window.innerHeight + icon.size) {
            newY = -icon.size;
          }

          return {
            ...icon,
            x: newX,
            y: newY,
            vx: newVx,
            vy: newVy,
            hue: (icon.hue + 0.5) % 360,
            directionChangeCounter: 0
          };
        });

        for (let i = 0; i < newIcons.length; i++) {
          for (let j = i + 1; j < newIcons.length; j++) {
            if (checkCollision(newIcons[i], newIcons[j])) {
              handleCollision(newIcons[i], newIcons[j]);
            }
          }
        }

        return newIcons;
      });

      requestAnimationFrame(animateIcons);
    };

    const animationId = requestAnimationFrame(animateIcons);
    return () => cancelAnimationFrame(animationId);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
      <canvas ref={canvasRef} className="absolute inset-0 z-[100] pointer-events-none" />
      
      {/* Floating devices with collision detection */}
      <div className="absolute inset-0 z-[100] opacity-30 pointer-events-none">
        {icons.map((icon, index) => {
          const IconComponent = [Smartphone, Phone, Laptop, Monitor, Headset, Wifi, Pen, BookOpen][index];
          return (
            <div
              key={icon.id}
              className="absolute transition-transform"
              style={{
                left: `${icon.x}px`,
                top: `${icon.y}px`,
                transform: 'translate(-50%, -50%)',
                filter: `hue-rotate(${icon.hue}deg)`
              }}
            >
              <IconComponent size={icon.size} />
            </div>
          );
        })}
      </div>

      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <div className="animate-fade-in">
          <h1 
            className="text-4xl md:text-6xl lg:text-8xl font-bold mb-3 md:mb-4 text-black"
            style={{ fontFamily: 'Crimson Text, serif', lineHeight: '1.1' }}
          >
            {t.hero.title}
          </h1>
          <h2 
            className="text-3xl md:text-5xl lg:text-7xl font-bold mb-6 md:mb-8 text-black"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            {t.hero.subtitle}
          </h2>
          <p className="text-base md:text-xl lg:text-2xl mb-8 md:mb-12 text-gray-700 max-w-3xl mx-auto">
            {t.hero.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={scrollToContact}
              className="w-full sm:w-72 h-16 px-6 bg-red-600 text-white rounded-lg font-semibold text-base hover:bg-red-700 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl border-2 border-black flex items-center justify-center"
            >
              {t.hero.cta}
            </button>
            <button
              onClick={scrollToAbout}
              className="w-full sm:w-72 h-16 px-6 bg-transparent text-black border-2 border-black rounded-lg font-semibold text-base hover:bg-black hover:text-white transition-all duration-200 hover:-translate-y-1 hover:shadow-xl flex items-center justify-center"
            >
              {t.hero.ctaSecondary}
            </button>
          </div>
        </div>
      </div>

    </section>
  );
};

export default Hero;